import system from '../theme';
import { DesktopHeader } from './DesktopHeader';
import { EnrollmentHeader } from './EnrollmentHeader';
import { getActiveTab, getNavItems, getVariantConfig, type NavVariant } from './navigationData';
import { MobileHeader } from './MobileHeader';
import { Stack, useBreakpointValue } from '@chakra-ui/react';
import { useEffect, useMemo } from 'react';
import { useFirebaseAuth } from '../hooks/useFirebaseAuth';
import { useLocation, useNavigate } from 'react-router-dom';
import { useNavigationBadges } from './NavigationBadgesContext';
import { useSignOut } from 'react-firebase-hooks/auth';
import { useUser } from '../hooks/useUser';
import { useUserClaims } from '../hooks/useUserClaims';

export function Navigation() {
    const location = useLocation();
    const navigate = useNavigate();
    const auth = useFirebaseAuth();

    const [user, userLoading] = useUser();
    const [userClaims] = useUserClaims(auth);
    const [signOut] = useSignOut(auth);
    const { counts } = useNavigationBadges();

    const isAdmin = !!userClaims?.admin;
    const isMobile = useBreakpointValue({ base: true, lg: false }) ?? false;
    const isEnrollmentRoute = location.pathname.startsWith('/zapisy');

    const shouldSkipHomeRedirect = useMemo(() => {
        const params = new URLSearchParams(location.search);
        const skipFlags = ['noRedirectToPanel'];
        const hasSkipFlag = skipFlags.some((flag) => params.has(flag));
        return hasSkipFlag;
    }, [location.search]);

    useEffect(() => {
        if (location.pathname === '/' && !userLoading && user && !shouldSkipHomeRedirect)
            navigate(isAdmin ? '/admin' : '/panel');
        if ((location.pathname.startsWith('/admin') || location.pathname.startsWith('/panel')) && !userLoading && !user)
            navigate('/login');
        if ((location.pathname.startsWith('/panel') && !userLoading && user && isAdmin))
            navigate('/admin');
        if ((location.pathname.startsWith('/admin') && !userLoading && user && !isAdmin))
            navigate('/panel');
    }, [location.pathname, navigate, user, userLoading, isAdmin, shouldSkipHomeRedirect]);

    const handleLogout = async () => {
        try {
            await signOut();
        } catch (err) {
            console.error(err);
        }
        navigate('/');
    };

    const variant = useMemo<NavVariant>(() => {
        const p = location.pathname;
        if (p === '/') return 'home';
        if (p.startsWith('/polityka-prywatnosci') || p.startsWith('/ochrona-maloletnich')) return 'legal';
        if (p.startsWith('/zapisy')) return 'enrollment-basic';
        if (p.startsWith('/panel')) return 'client';
        if (p.startsWith('/admin')) return 'admin';
        if (p.startsWith('/login')) return 'basic';
        return 'basic';
    }, [location.pathname]);

    const config = useMemo(() => getVariantConfig(variant), [variant]);

    const navItems = useMemo(
        () => getNavItems({ isAdmin, pathname: location.pathname, badgeCounts: counts }),
        [counts, isAdmin, location.pathname],
    );

    const activeTab = useMemo(
        () => getActiveTab({ navItems, pathname: location.pathname, hash: location.hash }),
        [navItems, location.pathname, location.hash],
    );

    const shouldUseMobileHeader = isMobile;

    return (
        <Stack
            pt={2}
            pb={config.showTabs || (config.showTabsOnMobile && isMobile) ? 0 : 2}
            as="header"
            position="relative"
            zIndex={system.token('zIndices.docked')}
            boxShadow="0px 1px 2px 0px rgb(216, 216, 216)"
            gap={0}
            >
            {isEnrollmentRoute ? <EnrollmentHeader /> : (
                shouldUseMobileHeader ? (
                    <MobileHeader
                        navItems={navItems}
                        activeTab={activeTab}
                        user={user}
                        isAdmin={isAdmin}
                        userLoading={userLoading}
                        locationPathname={location.pathname}
                        config={config}
                        navigate={navigate}
                        handleLogout={handleLogout}
                    />
                ) : (
                    <DesktopHeader
                        config={config}
                        navItems={navItems}
                        activeTab={activeTab}
                        user={user}
                        userLoading={userLoading}
                        isAdmin={isAdmin}
                        locationPathname={location.pathname}
                        navigate={navigate}
                        handleLogout={handleLogout}
                    />
                )
            )}
        </Stack>
    );
}
